import { useEffect } from "react";
import { createRootRoute, createRoute, createLazyRoute, useNavigate, type AnyRoute } from "@tanstack/react-router";
import { RootLayout } from "./routes/__root";

const rootRoute = createRootRoute({ component: RootLayout });

function route(path: string): AnyRoute {
  return createRoute({ getParentRoute: () => rootRoute, path });
}

const indexRoute = route("/").lazy(() =>
  import("./routes/index").then((m) => createLazyRoute("/")({ component: m.HomePage })),
);
const salesRoute = route("/sales").lazy(() =>
  import("./routes/sales").then((m) => createLazyRoute("/sales")({ component: m.SalesPage })),
);
const billsRoute = route("/bills").lazy(() =>
  import("./routes/bills").then((m) => createLazyRoute("/bills")({ component: m.BillsPage })),
);
const appointmentsRoute = route("/appointments").lazy(() =>
  import("./routes/appointments").then((m) =>
    createLazyRoute("/appointments")({ component: m.AppointmentsPage }),
  ),
);
const clientsRoute = route("/clients").lazy(() =>
  import("./routes/clients").then((m) => createLazyRoute("/clients")({ component: m.ClientsPage })),
);
const servicesRoute = route("/services").lazy(() =>
  import("./routes/services").then((m) => createLazyRoute("/services")({ component: m.ServicesPage })),
);
const inventoryRoute = route("/inventory").lazy(() =>
  import("./routes/inventory").then((m) => createLazyRoute("/inventory")({ component: m.InventoryPage })),
);
const purchasingRoute = route("/purchasing").lazy(() =>
  import("./routes/purchasing").then((m) => createLazyRoute("/purchasing")({ component: m.PurchasingPage })),
);
const customersRoute = route("/customers").lazy(() =>
  import("./routes/customers").then((m) => createLazyRoute("/customers")({ component: m.CustomersPage })),
);
const marketingRoute = route("/marketing").lazy(() =>
  import("./routes/marketing").then((m) => createLazyRoute("/marketing")({ component: m.MarketingPage })),
);
const storeRoute = route("/stores/$storeId").lazy(() =>
  import("./routes/stores").then((m) => createLazyRoute("/stores/$storeId")({ component: m.StorePage })),
);
const storesRoute = route("/stores").lazy(() =>
  import("./routes/stores").then((m) => createLazyRoute("/stores")({ component: m.StoresPage })),
);
const analyticsRoute = route("/analytics").lazy(() =>
  import("./routes/analytics").then((m) => createLazyRoute("/analytics")({ component: m.AnalyticsPage })),
);
const tablesRoute = route("/tables").lazy(() =>
  import("./routes/tables").then((m) => createLazyRoute("/tables")({ component: m.TablesPage })),
);
const menuRoute = route("/menu").lazy(() =>
  import("./routes/menu").then((m) => createLazyRoute("/menu")({ component: m.MenuPage })),
);
const ingredientsRoute = route("/ingredients").lazy(() =>
  import("./routes/ingredients").then((m) => createLazyRoute("/ingredients")({ component: m.IngredientsPage })),
);
const staffRoute = route("/staff").lazy(() =>
  import("./routes/staff").then((m) => createLazyRoute("/staff")({ component: m.StaffPage })),
);
// Access Control was folded into the Staff page's tabs (see routes/staff.tsx)
// — this route just sends any old bookmarked/linked "/access" URL there.
function AccessRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to: "/staff", replace: true });
  }, [navigate]);
  return <></>;
}
const accessRoute = createRoute({ getParentRoute: () => rootRoute, path: "/access", component: AccessRedirect });
const payrollRoute = route("/payroll").lazy(() =>
  import("./routes/payroll").then((m) => createLazyRoute("/payroll")({ component: m.PayrollPage })),
);
const accountRoute = route("/account").lazy(() =>
  import("./routes/settings").then((m) => createLazyRoute("/account")({ component: m.SettingsPage })),
);
const settingsRoute = route("/settings").lazy(() =>
  import("./routes/settings").then((m) => createLazyRoute("/settings")({ component: m.SettingsPage })),
);
const receiptDesignRoute = route("/settings/receipt").lazy(() =>
  import("./routes/receipt-design").then((m) => createLazyRoute("/settings/receipt")({ component: m.ReceiptDesignPage })),
);
const devicesRoute = route("/devices").lazy(() =>
  import("./routes/devices").then((m) => createLazyRoute("/devices")({ component: m.DevicesPage })),
);
const printHubsRoute = route("/print-hubs").lazy(() =>
  import("./routes/print-hubs").then((m) => createLazyRoute("/print-hubs")({ component: m.PrintHubsPage })),
);
const modulesRoute = route("/modules").lazy(() =>
  import("./routes/modules").then((m) => createLazyRoute("/modules")({ component: m.ModulesPage })),
);
const billingRoute = route("/billing").lazy(() =>
  import("./routes/billing").then((m) => createLazyRoute("/billing")({ component: m.BillingPage })),
);
const paymentsRoute = route("/payments").lazy(() =>
  import("./routes/payments").then((m) => createLazyRoute("/payments")({ component: m.PaymentsPage })),
);
const guestPortalRoute = route("/guest-portal").lazy(() =>
  import("./routes/guest-portal").then((m) => createLazyRoute("/guest-portal")({ component: m.GuestPortalPage })),
);
const moreRoute = route("/more").lazy(() =>
  import("./routes/more").then((m) => createLazyRoute("/more")({ component: m.MorePage })),
);
const integrationsRoute = route("/integrations").lazy(() =>
  import("./routes/integrations").then((m) => createLazyRoute("/integrations")({ component: m.IntegrationsPage })),
);

export const routeTree = rootRoute.addChildren([
  indexRoute,
  salesRoute,
  billsRoute,
  appointmentsRoute,
  clientsRoute,
  servicesRoute,
  inventoryRoute,
  purchasingRoute,
  customersRoute,
  marketingRoute,
  storesRoute,
  storeRoute,
  analyticsRoute,
  tablesRoute,
  menuRoute,
  ingredientsRoute,
  staffRoute,
  accessRoute,
  payrollRoute,
  settingsRoute,
  receiptDesignRoute,
  accountRoute,
  devicesRoute,
  printHubsRoute,
  modulesRoute,
  billingRoute,
  paymentsRoute,
  guestPortalRoute,
  integrationsRoute,
  moreRoute,
]);
